import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import {
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { Coupon as ApiCoupon } from '../../../../api/promotion-service/model/coupon';
import { CouponDTO } from '../../../../api/promotion-service/model/couponDTO';
import { CouponFilterDTO } from '../../../../api/promotion-service/model/couponFilterDTO';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Custom validator for start date to ensure it's not in the past (Add mode only)
export function startDatePastValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) {
    return null; // Don't validate if no value
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = new Date(control.value);
  startDate.setHours(0, 0, 0, 0);

  if (startDate < today) {
    return { startDatePast: true };
  }

  return null;
}

// Custom validator for end date to ensure it's not in the past (Add mode only)
export function endDatePastValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) {
    return null; // Don't validate if no value
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(control.value);
  endDate.setHours(0, 0, 0, 0);

  if (endDate < today) {
    return { endDatePast: true };
  }

  return null;
}

// Alternative approach: Separate validators for add and edit modes (Best Practice)
// This is more explicit and reliable than trying to detect edit mode

// Helper function to create edit mode validators with original dates
export function createEditModeValidators(
  originalStartDate?: Date,
  originalEndDate?: Date
) {
  return {
    startDateValidators: [
      startDatePastValidatorEditMode(originalStartDate),
      startDateAfterEndDateValidator,
    ],
    endDateValidators: [
      endDatePastValidatorEditMode(originalEndDate),
      endDateAfterStartDateValidator,
    ],
  };
}

// Smart validator for edit mode - allows original dates but blocks new past dates
export function startDatePastValidatorEditMode(originalStartDate?: Date) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const currentStartDate = control.value;

    // If there's no original date, treat as add mode (block past dates)
    if (!originalStartDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(currentStartDate);
      startDate.setHours(0, 0, 0, 0);

      if (startDate < today) {
        return { startDatePast: true };
      }
      return null;
    }

    // Compare with original date (including time)
    const original = new Date(originalStartDate);
    const current = new Date(currentStartDate);

    // If the new date is the same as original (including time), allow it (even if past)
    const timeDiff = Math.abs(current.getTime() - original.getTime());
    if (timeDiff < 1000) {
      // Less than 1 second difference (same date and time)
      return null; // Allow original date
    }

    // If the new date is different from original, validate it's not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);

    if (currentDateOnly < today) {
      return { startDatePast: true };
    }

    return null;
  };
}

// Smart validator for edit mode - allows original dates but blocks new past dates
export function endDatePastValidatorEditMode(originalEndDate?: Date) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const currentEndDate = control.value;

    // If there's no original date, treat as add mode (block past dates)
    if (!originalEndDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const endDate = new Date(currentEndDate);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < today) {
        return { endDatePast: true };
      }
      return null;
    }

    // Compare with original date (including time)
    const original = new Date(originalEndDate);
    const current = new Date(currentEndDate);

    // If the new date is the same as original (including time), allow it (even if past)
    const timeDiff = Math.abs(current.getTime() - original.getTime());
    if (timeDiff < 1000) {
      // Less than 1 second difference (same date and time)
      return null; // Allow original date
    }

    // If the new date is different from original, validate it's not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);

    if (currentDateOnly < today) {
      return { endDatePast: true };
    }

    return null;
  };
}

// Custom validator for start date to ensure it's not after end date
export function startDateAfterEndDateValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.parent) {
    return null;
  }

  const startDate = control.value;
  const endDate = control.parent.get('endDate')?.value;

  if (!startDate || !endDate) {
    return null; // Don't validate if either date is missing
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start >= end) {
    // Set error on both fields
    const endDateControl = control.parent.get('endDate');
    if (endDateControl) {
      endDateControl.setErrors({
        ...endDateControl.errors,
        endDateBeforeStart: true,
      });
    }
    return { startDateAfterEnd: true };
  } else {
    // Clear error from end date when relationship is valid
    const endDateControl = control.parent.get('endDate');
    if (endDateControl && endDateControl.errors?.['endDateBeforeStart']) {
      delete endDateControl.errors['endDateBeforeStart'];
      if (Object.keys(endDateControl.errors).length === 0) {
        endDateControl.setErrors(null);
      }
    }
    return null;
  }
}

// Custom validator for end date to ensure it's after start date
export function endDateAfterStartDateValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.parent) {
    return null;
  }

  const startDate = control.parent.get('startDate')?.value;
  const endDate = control.value;

  if (!startDate || !endDate) {
    return null; // Don't validate if either date is missing
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (end <= start) {
    // Set error on both fields
    const startDateControl = control.parent.get('startDate');
    if (startDateControl) {
      startDateControl.setErrors({
        ...startDateControl.errors,
        startDateAfterEnd: true,
      });
    }
    return { endDateBeforeStart: true };
  } else {
    // Clear error from start date when relationship is valid
    const startDateControl = control.parent.get('startDate');
    if (startDateControl && startDateControl.errors?.['startDateAfterEnd']) {
      delete startDateControl.errors['startDateAfterEnd'];
      if (Object.keys(startDateControl.errors).length === 0) {
        startDateControl.setErrors(null);
      }
    }
    return null;
  }
}

// Custom validator for percentage discount values (simplified)
export function percentageDiscountValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.parent) {
    return null;
  }

  const discountValue = control.value;
  const discountType = control.parent.get('discountType')?.value;
  const couponType = control.parent.get('couponType')?.value;

  // Only validate if it's a percentage discount
  if (discountType === 'PERCENTAGE' || couponType === 'PERCENTAGE_DISCOUNT') {
    if (
      discountValue === null ||
      discountValue === undefined ||
      discountValue === ''
    ) {
      return null; // Let required validator handle empty values
    }

    const numValue = Number(discountValue);

    // Simple validation: must be greater than 0 and less than or equal to 100
    if (isNaN(numValue) || numValue <= 0 || numValue > 100) {
      return { percentageInvalid: true };
    }
  }

  return null;
}

// Coupon status constants
export const COUPON_STATUS = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
  ENDED: 'ENDED',
} as const;

export type CouponStatus = (typeof COUPON_STATUS)[keyof typeof COUPON_STATUS];

// Type guard to check if a string is a valid coupon status
export function isCouponStatus(status: string): status is CouponStatus {
  return Object.values(COUPON_STATUS).includes(status as CouponStatus);
}

// Helper function to get status display text (returns translation key)
export function getCouponStatusDisplayText(status: CouponStatus): string {
  switch (status) {
    case COUPON_STATUS.PENDING_APPROVAL:
      return 'coupon.status.pending_approval';
    case COUPON_STATUS.ACTIVE:
      return 'coupon.status.active';
    case COUPON_STATUS.REJECTED:
      return 'coupon.status.rejected';
    case COUPON_STATUS.INACTIVE:
      return 'coupon.status.inactive';
    case COUPON_STATUS.EXPIRED:
      return 'coupon.status.expired';
    case COUPON_STATUS.ENDED:
      return 'coupon.status.ended';
    default:
      return status;
  }
}

// Helper function to check if status is active
export function isCouponStatusActive(status: CouponStatus): boolean {
  return status === COUPON_STATUS.ACTIVE;
}

// Helper function to check if status is inactive
export function isCouponStatusInactive(status: CouponStatus): boolean {
  return (
    status === COUPON_STATUS.INACTIVE ||
    status === COUPON_STATUS.REJECTED ||
    status === COUPON_STATUS.EXPIRED ||
    status === COUPON_STATUS.ENDED
  );
}

// Helper function to get status color for UI
export function getCouponStatusColor(status: CouponStatus): string {
  switch (status) {
    case COUPON_STATUS.PENDING_APPROVAL:
      return 'warning';
    case COUPON_STATUS.ACTIVE:
      return 'success';
    case COUPON_STATUS.REJECTED:
      return 'danger';
    case COUPON_STATUS.INACTIVE:
      return 'secondary';
    case COUPON_STATUS.EXPIRED:
      return 'danger';
    case COUPON_STATUS.ENDED:
      return 'info';
    default:
      return 'secondary';
  }
}

// Status options for dropdowns and forms
export const COUPON_STATUS_OPTIONS = [
  {
    label: 'coupon.status.pending_approval',
    value: COUPON_STATUS.PENDING_APPROVAL,
  },
  { label: 'coupon.status.active', value: COUPON_STATUS.ACTIVE },
  { label: 'coupon.status.rejected', value: COUPON_STATUS.REJECTED },
  { label: 'coupon.status.inactive', value: COUPON_STATUS.INACTIVE },
  { label: 'coupon.status.expired', value: COUPON_STATUS.EXPIRED },
  { label: 'coupon.status.ended', value: COUPON_STATUS.ENDED },
];

// ============================================================================
// INTERFACES
// ============================================================================

// Use the API model directly
export type Coupon = ApiCoupon;

// Use the API filter DTO directly
export type CouponSearchParams = CouponFilterDTO;

// ============================================================================
// ENUMS - Use API model enums
// ============================================================================

// Re-export API enums for convenience
export const CouponType = ApiCoupon.CouponTypeEnum;
export const DiscountType = ApiCoupon.DiscountTypeEnum;

// Use CouponFilterDTO enums for search filters
export const CouponFilterType = CouponFilterDTO.CouponTypeEnum;
export const CouponFilterDiscountType = CouponFilterDTO.DiscountTypeEnum;

// ============================================================================
// OPTIONS AND CONSTANTS
// ============================================================================

// Temporary limited options for coupon types (only 2 options for now)
export const COUPON_TYPE_OPTIONS = [
  {
    label: 'coupon.options.coupon_types.percentage_discount',
    value: 'PERCENTAGE_DISCOUNT',
  },
  {
    label: 'coupon.options.coupon_types.flat_discount',
    value: 'FLAT_DISCOUNT',
  },
];

// Full options for future use (commented out temporarily)
/*
export const COUPON_TYPE_OPTIONS_FULL = [
  {
    label: 'coupon.options.coupon_types.percentage_discount',
    value: 'PERCENTAGE_DISCOUNT',
  },
  {
    label: 'coupon.options.coupon_types.flat_discount',
    value: 'FLAT_DISCOUNT',
  },
  {
    label: 'coupon.options.coupon_types.seasonal_event',
    value: 'SEASONAL_EVENT',
  },
  { label: 'coupon.options.coupon_types.gamification', value: 'GAMIFICATION' },
  {
    label: 'coupon.options.coupon_types.channel_specific',
    value: 'CHANNEL_SPECIFIC',
  },
  {
    label: 'coupon.options.coupon_types.payment_partner',
    value: 'PAYMENT_PARTNER',
  },
  { label: 'coupon.options.coupon_types.buy_x_get_y', value: 'BUY_X_GET_Y' },
  { label: 'coupon.options.coupon_types.cashback', value: 'CASHBACK' },
  {
    label: 'coupon.options.coupon_types.membership_pricing',
    value: 'MEMBERSHIP_PRICING',
  },
  {
    label: 'coupon.options.coupon_types.tiered_discount',
    value: 'TIERED_DISCOUNT',
  },
  {
    label: 'coupon.options.coupon_types.conditional_discount',
    value: 'CONDITIONAL_DISCOUNT',
  },
  {
    label: 'coupon.options.coupon_types.mix_and_match',
    value: 'MIX_AND_MATCH',
  },
  {
    label: 'coupon.options.coupon_types.combo_bundle_pricing',
    value: 'COMBO_BUNDLE_PRICING',
  },
  {
    label: 'coupon.options.coupon_types.coupon_voucher',
    value: 'COUPON_VOUCHER',
  },
  {
    label: 'coupon.options.coupon_types.points_loyalty_rewards',
    value: 'POINTS_LOYALTY_REWARDS',
  },
  {
    label: 'coupon.options.coupon_types.discount_on_total_bill',
    value: 'DISCOUNT_ON_TOTAL_BILL',
  },
];
*/

export const DISCOUNT_TYPE_OPTIONS = [
  {
    label: 'coupon.options.discount_types.percentage',
    value: CouponFilterDiscountType.Percentage,
  },
  {
    label: 'coupon.options.discount_types.fixed_amount',
    value: CouponFilterDiscountType.FixedAmount,
  },
];

// Define the relationship between coupon types and discount types
// This ensures logical consistency for different coupon types
// Temporary mapping for only 2 coupon types
export const COUPON_DISCOUNT_TYPE_MAPPING = {
  PERCENTAGE_DISCOUNT: [DiscountType.Percentage],
  FLAT_DISCOUNT: [DiscountType.FixedAmount],
};

// Full mapping for future use (commented out temporarily)
/*
export const COUPON_DISCOUNT_TYPE_MAPPING_FULL = {
  PERCENTAGE_DISCOUNT: [DiscountType.Percentage],
  FLAT_DISCOUNT: [DiscountType.FixedAmount],
  SEASONAL_EVENT: [DiscountType.Percentage, DiscountType.FixedAmount],
  GAMIFICATION: [DiscountType.Percentage, DiscountType.FixedAmount],
  CHANNEL_SPECIFIC: [DiscountType.Percentage, DiscountType.FixedAmount],
  PAYMENT_PARTNER: [DiscountType.Percentage, DiscountType.FixedAmount],
  BUY_X_GET_Y: [DiscountType.Percentage, DiscountType.FixedAmount],
  CASHBACK: [DiscountType.Percentage, DiscountType.FixedAmount],
  MEMBERSHIP_PRICING: [DiscountType.Percentage, DiscountType.FixedAmount],
  TIERED_DISCOUNT: [DiscountType.Percentage, DiscountType.FixedAmount],
  CONDITIONAL_DISCOUNT: [DiscountType.Percentage, DiscountType.FixedAmount],
  MIX_AND_MATCH: [DiscountType.Percentage, DiscountType.FixedAmount],
  COMBO_BUNDLE_PRICING: [DiscountType.Percentage, DiscountType.FixedAmount],
  COUPON_VOUCHER: [DiscountType.Percentage, DiscountType.FixedAmount],
  POINTS_LOYALTY_REWARDS: [DiscountType.Percentage, DiscountType.FixedAmount],
  DISCOUNT_ON_TOTAL_BILL: [DiscountType.Percentage, DiscountType.FixedAmount],
};
*/

// Helper function to get valid discount types for a coupon type
export function getValidDiscountTypes(couponType: string): string[] {
  return (COUPON_DISCOUNT_TYPE_MAPPING as any)[couponType] || [];
}

// Helper function to get valid coupon types for a discount type
export function getValidCouponTypes(discountType: string): string[] {
  return Object.keys(COUPON_DISCOUNT_TYPE_MAPPING).filter((couponType) =>
    (COUPON_DISCOUNT_TYPE_MAPPING as any)[couponType].includes(discountType)
  );
}

// Helper function to get coupon type from discount type (for auto-update)
export function getCouponTypeFromDiscountType(discountType: string): string {
  const mapping: { [key: string]: string } = {
    PERCENTAGE: 'PERCENTAGE_DISCOUNT',
    FIXED_AMOUNT: 'FLAT_DISCOUNT',
  };
  return mapping[discountType] || '';
}

// Removed duplicate - using the new status options below

export const CHANNEL_OPTIONS = [
  { label: 'Online Store', value: 'ONLINE_STORE' },
  { label: 'Retail Store', value: 'RETAIL_STORE' },
  { label: 'Marketplace', value: 'MARKETPLACE' },
  { label: 'Social Commerce', value: 'SOCIAL_COMMERCE' },
  { label: 'Mobile App', value: 'MOBILE_APP' },
];

export const CUSTOMER_GROUP_OPTIONS = [
  { label: 'All Customers', value: 'ALL_CUSTOMERS' },
  { label: 'New Customers', value: 'NEW_CUSTOMERS' },
  { label: 'Returning Customers', value: 'RETURNING_CUSTOMERS' },
  { label: 'VIP Customers', value: 'VIP_CUSTOMERS' },
  { label: 'Wholesale Customers', value: 'WHOLESALE_CUSTOMERS' },
];

// ============================================================================
// FORM FIELD CONFIGURATIONS
// ============================================================================

// Form fields for add/edit pages (includes status field for edit)
export const COUPON_FORM_FIELDS: FormField[] = [
  {
    label: 'coupon.form.fields.coupon_code',
    controlName: 'couponCode',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_coupon_code',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    maxLength: 50,
  },
  {
    label: 'coupon.form.fields.coupon_name',
    controlName: 'couponName',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_coupon_name',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    maxLength: 100,
  },
  {
    label: 'coupon.form.fields.description',
    controlName: 'description',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_description',
    section: 'coupon.form.sections.coupon_information',
    maxLength: 500,
  },
  {
    label: 'coupon.form.fields.coupon_type',
    controlName: 'couponType',
    type: 'dropdown',
    placeholder: 'coupon.form.placeholders.select_coupon_type',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    options: COUPON_TYPE_OPTIONS,
  },
  {
    label: 'coupon.form.fields.discount_type',
    controlName: 'discountType',
    type: 'dropdown',
    placeholder: 'coupon.form.placeholders.select_discount_type',
    required: false, // Not required - auto-populated based on coupon type
    hidden: true, // Hidden from UI - auto-populated based on coupon type
    section: 'coupon.form.sections.coupon_information',
    options: DISCOUNT_TYPE_OPTIONS,
    disabled: true, // Disabled - auto-populated based on coupon type
  },
  {
    label: 'coupon.form.fields.discount_value',
    controlName: 'discountValue',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_discount_value',
    required: true,
    section: 'coupon.form.sections.discount_information',
    min: 0,
    decimal: true,
    validators: [percentageDiscountValidator],
    errorMessages: {
      percentageInvalid: 'coupon.validation.percentage_invalid',
    },
    dynamicLabel: {
      type: 'custom',
      dependsOn: 'discountType',
      rules: [
        {
          condition: (value) => value === 'PERCENTAGE',
          label: 'coupon.form.fields.discount_value_percentage',
        },
        {
          condition: (value) => value === 'FIXED_AMOUNT',
          label: 'coupon.form.fields.discount_value_fixed',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.minimum_order_amount',
    controlName: 'minOrderAmount',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_minimum_order_amount',
    section: 'coupon.form.sections.discount_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
    dynamicLabel: {
      type: 'custom',
      rules: [
        {
          condition: () => true,
          label: 'coupon.form.fields.minimum_order_amount_vnd',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.maximum_discount_amount',
    controlName: 'maxDiscountAmount',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_maximum_discount_amount',
    section: 'coupon.form.sections.discount_information',
    min: 0,
    dependsOn: 'couponType',
    dynamicLabel: {
      type: 'custom',
      rules: [
        {
          condition: () => true,
          label: 'coupon.form.fields.maximum_discount_amount_vnd',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.start_date',
    controlName: 'startDate',
    type: 'date',
    placeholder: 'coupon.form.placeholders.select_start_date',
    required: true,
    section: 'coupon.form.sections.date_information',
    showTime: true,
    hourFormat: '24',
    minDate: null, // Will be set dynamically based on edit mode
    validators: [startDateAfterEndDateValidator],
    errorMessages: {
      startDateAfterEnd: 'common.validation.start_date_after_end',
    },
  },
  {
    label: 'coupon.form.fields.end_date',
    controlName: 'endDate',
    type: 'date',
    placeholder: 'coupon.form.placeholders.select_end_date',
    required: true,
    section: 'coupon.form.sections.date_information',
    showTime: true,
    hourFormat: '24',
    minDate: null, // Will be set dynamically based on edit mode
    validators: [endDateAfterStartDateValidator],
    errorMessages: {
      endDateBeforeStart: 'common.validation.end_date_before_start',
    },
  },
  {
    label: 'coupon.form.fields.usage_limit',
    controlName: 'usageLimit',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_usage_limit',
    section: 'coupon.form.sections.limit_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
  },
  {
    label: 'coupon.form.fields.per_customer_limit',
    controlName: 'perCustomerLimit',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_per_customer_limit',
    section: 'coupon.form.sections.limit_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
  },
  {
    label: 'coupon.form.fields.status',
    controlName: 'status',
    type: 'dropdown',
    placeholder: 'coupon.form.placeholders.select_status',
    section: 'coupon.form.sections.coupon_information',
    options: COUPON_STATUS_OPTIONS,
    required: true,
    disabled: true,
  },
];

// Form fields for add page only (excludes status field)
export const COUPON_ADD_FORM_FIELDS: FormField[] = [
  {
    label: 'coupon.form.fields.coupon_code',
    controlName: 'couponCode',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_coupon_code',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    maxLength: 50,
  },
  {
    label: 'coupon.form.fields.coupon_name',
    controlName: 'couponName',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_coupon_name',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    maxLength: 100,
  },
  {
    label: 'coupon.form.fields.description',
    controlName: 'description',
    type: 'text',
    placeholder: 'coupon.form.placeholders.enter_description',
    section: 'coupon.form.sections.coupon_information',
    maxLength: 500,
  },
  {
    label: 'coupon.form.fields.coupon_type',
    controlName: 'couponType',
    type: 'dropdown',
    placeholder: 'coupon.form.placeholders.select_coupon_type',
    required: true,
    section: 'coupon.form.sections.coupon_information',
    options: COUPON_TYPE_OPTIONS,
  },
  {
    label: 'coupon.form.fields.discount_type',
    controlName: 'discountType',
    type: 'dropdown',
    placeholder: 'coupon.form.placeholders.select_discount_type',
    required: false, // Not required - auto-populated based on coupon type
    hidden: true, // Hidden from UI - auto-populated based on coupon type
    section: 'coupon.form.sections.coupon_information',
    options: DISCOUNT_TYPE_OPTIONS,
    disabled: true, // Disabled - auto-populated based on coupon type
  },
  {
    label: 'coupon.form.fields.discount_value',
    controlName: 'discountValue',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_discount_value',
    required: true,
    section: 'coupon.form.sections.discount_information',
    min: 0,
    decimal: true,
    validators: [percentageDiscountValidator],
    errorMessages: {
      percentageInvalid: 'coupon.validation.percentage_invalid',
    },
    dynamicLabel: {
      type: 'custom',
      dependsOn: 'discountType',
      rules: [
        {
          condition: (value) => value === 'PERCENTAGE',
          label: 'coupon.form.fields.discount_value_percentage',
        },
        {
          condition: (value) => value === 'FIXED_AMOUNT',
          label: 'coupon.form.fields.discount_value_fixed',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.minimum_order_amount',
    controlName: 'minOrderAmount',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_minimum_order_amount',
    section: 'coupon.form.sections.discount_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
    dynamicLabel: {
      type: 'custom',
      rules: [
        {
          condition: () => true,
          label: 'coupon.form.fields.minimum_order_amount_vnd',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.maximum_discount_amount',
    controlName: 'maxDiscountAmount',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_maximum_discount_amount',
    section: 'coupon.form.sections.discount_information',
    min: 0,
    dependsOn: 'couponType',
    dynamicLabel: {
      type: 'custom',
      rules: [
        {
          condition: () => true,
          label: 'coupon.form.fields.maximum_discount_amount_vnd',
        },
      ],
    },
  },
  {
    label: 'coupon.form.fields.start_date',
    controlName: 'startDate',
    type: 'date',
    placeholder: 'coupon.form.placeholders.select_start_date',
    required: true,
    section: 'coupon.form.sections.date_information',
    showTime: true,
    hourFormat: '24',
    minDate: new Date(), // Will be set dynamically based on edit mode
    validators: [startDatePastValidator, startDateAfterEndDateValidator],
    errorMessages: {
      startDatePast: 'common.validation.start_date_past',
      startDateAfterEnd: 'common.validation.start_date_after_end',
    },
  },
  {
    label: 'coupon.form.fields.end_date',
    controlName: 'endDate',
    type: 'date',
    placeholder: 'coupon.form.placeholders.select_end_date',
    required: true,
    section: 'coupon.form.sections.date_information',
    showTime: true,
    hourFormat: '24',
    minDate: new Date(), // Will be set dynamically based on edit mode
    validators: [endDatePastValidator, endDateAfterStartDateValidator],
    errorMessages: {
      endDatePast: 'common.validation.end_date_past',
      endDateBeforeStart: 'common.validation.end_date_before_start',
    },
  },
  {
    label: 'coupon.form.fields.usage_limit',
    controlName: 'usageLimit',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_usage_limit',
    section: 'coupon.form.sections.limit_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
  },
  {
    label: 'coupon.form.fields.per_customer_limit',
    controlName: 'perCustomerLimit',
    type: 'number',
    placeholder: 'coupon.form.placeholders.enter_per_customer_limit',
    section: 'coupon.form.sections.limit_information',
    min: 0,
    errorMessages: {
      invalid: 'common.validation.invalid',
      min: 'common.validation.min',
    },
  },
];

export const COUPON_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'coupon.form.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
  {
    label: 'coupon.form.actions.save',
    action: 'save',
    severity: 'info',
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const COUPON_DEFAULT_COLUMNS = {
  COUPON_CODE: 'couponCode',
  COUPON_NAME: 'couponName',
  COUPON_TYPE: 'couponType',
  DESCRIPTION: 'description',
  DISCOUNT_VALUE: 'discountValue',
  MIN_ORDER_AMOUNT: 'minOrderAmount',
  MAX_DISCOUNT_AMOUNT: 'maxDiscountAmount',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  USAGE_LIMIT: 'usageLimit',
  PER_CUSTOMER_LIMIT: 'perCustomerLimit',
  STATUS: 'status',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const COUPON_FLEXIBLE_COLUMNS = {} as const;

/**
 * Action column property name
 */
export const COUPON_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const COUPON_DEFAULT_COLUMNS_ORDER: string[] = [
  COUPON_DEFAULT_COLUMNS.COUPON_CODE,
  COUPON_DEFAULT_COLUMNS.COUPON_NAME,
  COUPON_DEFAULT_COLUMNS.COUPON_TYPE,
  COUPON_DEFAULT_COLUMNS.DESCRIPTION,
  COUPON_DEFAULT_COLUMNS.DISCOUNT_VALUE,
  COUPON_DEFAULT_COLUMNS.MIN_ORDER_AMOUNT,
  COUPON_DEFAULT_COLUMNS.MAX_DISCOUNT_AMOUNT,
  COUPON_DEFAULT_COLUMNS.START_DATE,
  COUPON_DEFAULT_COLUMNS.END_DATE,
  COUPON_DEFAULT_COLUMNS.USAGE_LIMIT,
  COUPON_DEFAULT_COLUMNS.PER_CUSTOMER_LIMIT,
  COUPON_DEFAULT_COLUMNS.STATUS,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const COUPON_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const COUPON_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'coupon.table.columns.coupon_code',
    prop: COUPON_DEFAULT_COLUMNS.COUPON_CODE,
    width: 150,
    type: 'text',
  },
  {
    label: 'coupon.table.columns.coupon_name',
    prop: COUPON_DEFAULT_COLUMNS.COUPON_NAME,
    width: 200,
    type: 'text',
  },
  {
    label: 'coupon.table.columns.coupon_type',
    prop: COUPON_DEFAULT_COLUMNS.COUPON_TYPE,
    width: 150,
    type: 'custom',
  },
  {
    label: 'coupon.table.columns.description',
    prop: COUPON_DEFAULT_COLUMNS.DESCRIPTION,
    width: 150,
    type: 'text',
  },
  {
    label: 'coupon.table.columns.discount_value',
    prop: COUPON_DEFAULT_COLUMNS.DISCOUNT_VALUE,
    width: 120,
    type: 'custom',
    align: 'right',
  },
  {
    label: 'coupon.table.columns.min_order_amount',
    prop: COUPON_DEFAULT_COLUMNS.MIN_ORDER_AMOUNT,
    width: 150,
    type: 'custom',
    align: 'right',
  },
  {
    label: 'coupon.table.columns.max_discount_amount',
    prop: COUPON_DEFAULT_COLUMNS.MAX_DISCOUNT_AMOUNT,
    width: 150,
    type: 'custom',
    align: 'right',
  },
  {
    label: 'coupon.table.columns.start_date',
    prop: COUPON_DEFAULT_COLUMNS.START_DATE,
    width: 180,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'coupon.table.columns.end_date',
    prop: COUPON_DEFAULT_COLUMNS.END_DATE,
    width: 180,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'coupon.table.columns.usage_limit',
    prop: COUPON_DEFAULT_COLUMNS.USAGE_LIMIT,
    width: 120,
    type: 'custom',
    align: 'right',
  },
  {
    label: 'coupon.table.columns.per_customer_limit',
    prop: COUPON_DEFAULT_COLUMNS.PER_CUSTOMER_LIMIT,
    width: 150,
    type: 'custom',
    align: 'right',
  },
  {
    label: 'coupon.table.columns.status',
    prop: COUPON_DEFAULT_COLUMNS.STATUS,
    width: 160,
    type: 'status',
  },
  {
    label: 'coupon.table.columns.actions',
    prop: COUPON_ACTION_COLUMN.ACTIONS,
    width: 280,
    type: 'actions',
  },
];

// ============================================================================
// SEARCH CONFIGURATIONS
// ============================================================================

export const COUPON_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'couponCode',
    label: 'coupon.search.fields.coupon_code',
    type: 'text',
    placeholder: 'coupon.search.placeholders.enter_coupon_code',
  },
  {
    key: 'couponName',
    label: 'coupon.search.fields.coupon_name',
    type: 'text',
    placeholder: 'coupon.search.placeholders.enter_coupon_name',
  },
  {
    key: 'couponType',
    label: 'coupon.search.fields.coupon_type',
    type: 'select',
    placeholder: 'coupon.search.placeholders.select_coupon_type',
    options: COUPON_TYPE_OPTIONS, // Now only shows 2 options: Flat Discount and Percentage Discount
    showClear: true,
  },
  // {
  //   key: 'discountType',
  //   label: 'coupon.search.fields.discount_type',
  //   type: 'select',
  //   placeholder: 'coupon.search.placeholders.select_discount_type',
  //   options: DISCOUNT_TYPE_OPTIONS,
  //   showClear: true,
  // },
  {
    key: 'status',
    label: 'coupon.search.fields.status',
    type: 'multiselect',
    placeholder: 'coupon.search.placeholders.select_status',
    options: COUPON_STATUS_OPTIONS,
    showClear: true,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(amount);
}

export function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
}

export function formatAmountDisplay(
  amount: number,
  discountType: string
): string {
  if (discountType === DiscountType.Percentage) {
    return `${amount}%`;
  } else {
    return formatCurrency(amount);
  }
}

export function calculateDiscountAmount(
  orderAmount: number,
  discountValue: number,
  discountType: string,
  maxDiscount?: number
): number {
  let discountAmount = 0;

  if (discountType === DiscountType.Percentage) {
    discountAmount = (orderAmount * discountValue) / 100;
  } else if (discountType === DiscountType.FixedAmount) {
    discountAmount = discountValue;
  } else {
    // Free shipping - this would be calculated separately
    discountAmount = 0;
  }

  // Apply maximum discount cap if specified
  if (maxDiscount && discountAmount > maxDiscount) {
    discountAmount = maxDiscount;
  }

  return Math.min(discountAmount, orderAmount); // Cannot discount more than order amount
}

export function isCouponValid(
  coupon: Coupon,
  orderAmount: number,
  customerId?: string
): { valid: boolean; reason?: string } {
  const now = new Date();
  const startDate = coupon.startDate ? new Date(coupon.startDate) : null;
  const endDate = coupon.endDate ? new Date(coupon.endDate) : null;

  // Check if coupon is deleted
  if (coupon.deleteFlag === true) {
    return { valid: false, reason: 'Coupon has been deleted' };
  }

  // Check if coupon is active
  if (
    coupon.status &&
    isCouponStatus(coupon.status) &&
    isCouponStatusInactive(coupon.status)
  ) {
    return { valid: false, reason: 'Coupon is inactive' };
  }

  // Check validity period if dates are provided
  if (startDate && now < startDate) {
    return { valid: false, reason: 'Coupon has not started yet' };
  }

  if (endDate && now > endDate) {
    return { valid: false, reason: 'Coupon has expired' };
  }

  // Check minimum order amount if provided
  if (coupon.minOrderAmount && orderAmount < coupon.minOrderAmount) {
    return {
      valid: false,
      reason: `Minimum order amount is ${formatCurrency(
        coupon.minOrderAmount
      )}`,
    };
  }

  return { valid: true };
}

export function getCouponTypeDisplayText(type: string): string {
  const typeMap: { [key: string]: string } = {
    [CouponType.FlatDiscount]: 'coupon.types.coupon_types.FLAT_DISCOUNT',
    [CouponType.PercentageDiscount]:
      'coupon.types.coupon_types.PERCENTAGE_DISCOUNT',
    [CouponType.TieredDiscount]: 'coupon.types.coupon_types.TIERED_DISCOUNT',
    [CouponType.BuyXGetY]: 'coupon.types.coupon_types.BUY_X_GET_Y',
    [CouponType.ComboBundlePricing]:
      'coupon.types.coupon_types.COMBO_BUNDLE_PRICING',
    [CouponType.MixAndMatch]: 'coupon.types.coupon_types.MIX_AND_MATCH',
    [CouponType.DiscountOnTotalBill]:
      'coupon.types.coupon_types.DISCOUNT_ON_TOTAL_BILL',
    [CouponType.ConditionalDiscount]:
      'coupon.types.coupon_types.CONDITIONAL_DISCOUNT',
    [CouponType.MembershipPricing]:
      'coupon.types.coupon_types.MEMBERSHIP_PRICING',
    [CouponType.PointsLoyaltyRewards]:
      'coupon.types.coupon_types.POINTS_LOYALTY_REWARDS',
    [CouponType.ChannelSpecific]: 'coupon.types.coupon_types.CHANNEL_SPECIFIC',
    [CouponType.SeasonalEvent]: 'coupon.types.coupon_types.SEASONAL_EVENT',
    [CouponType.PaymentPartner]: 'coupon.types.coupon_types.PAYMENT_PARTNER',
    [CouponType.CouponVoucher]: 'coupon.types.coupon_types.COUPON_VOUCHER',
    [CouponType.Cashback]: 'coupon.types.coupon_types.CASHBACK',
    [CouponType.Gamification]: 'coupon.types.coupon_types.GAMIFICATION',
  };
  return typeMap[type] || type;
}

export function getDiscountTypeDisplayText(type: string): string {
  const typeMap: { [key: string]: string } = {
    [DiscountType.Percentage]: 'coupon.types.discount_types.PERCENTAGE',
    [DiscountType.FixedAmount]: 'coupon.types.discount_types.FIXED_AMOUNT',
    [DiscountType.FreeItem]: 'coupon.types.discount_types.FREE_ITEM',
    [DiscountType.FreeShipping]: 'coupon.types.discount_types.FREE_SHIPPING',
    [DiscountType.TieredPercentage]:
      'coupon.types.discount_types.TIERED_PERCENTAGE',
    [DiscountType.TieredFixedAmount]:
      'coupon.types.discount_types.TIERED_FIXED_AMOUNT',
    [DiscountType.BundlePrice]: 'coupon.types.discount_types.BUNDLE_PRICE',
    [DiscountType.MixMatchPrice]: 'coupon.types.discount_types.MIX_MATCH_PRICE',
    [DiscountType.CashbackPercentage]:
      'coupon.types.discount_types.CASHBACK_PERCENTAGE',
    [DiscountType.CashbackFixedAmount]:
      'coupon.types.discount_types.CASHBACK_FIXED_AMOUNT',
    [DiscountType.PointsEarn]: 'coupon.types.discount_types.POINTS_EARN',
    [DiscountType.PointsRedeem]: 'coupon.types.discount_types.POINTS_REDEEM',
  };
  return typeMap[type] || type;
}
